import React, { useEffect, useMemo, useState } from 'react';
import { ColumnFilter } from '@tanstack/table-core';
import { Stack } from '@fiverr-private/layout_components';
import { TableProps } from '../../../../types';
import { TableFilter } from './components/TableFilter';
import { TableFilterControl } from './components/TableFilterControls';

export const TableFilterGroup = <T,>({ table }: Pick<TableProps<T>, 'table'>) => {
  const [filters, setFilters] = useState<ColumnFilter[]>([]);
  const sortedFilters = useMemo(
    () =>
      table
        .getHeaderGroups()
        .map((group) =>
          group.headers
            .filter((header) => header.column.getCanFilter())
            .sort((a, b) =>
              (a.column.columnDef.meta?.filter?.index || 0) > (b.column.columnDef?.meta?.filter?.index || 0) ? 1 : 0
            )
        ),
    [table]
  );

  useEffect(() => {
    const columnFilters = table.getState().columnFilters?.filter((f) => {
      if (Array.isArray(f.value)) {
        return f.value.length;
      }
      return f.id;
    });

    if (columnFilters.length) {
      setFilters([...columnFilters, { id: '', value: '' }]);
    } else {
      setFilters(columnFilters);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [table.getState()]);

  const onFilterClear = (filterId: string, value?: string) => {
    if (!filterId) {
      table.resetColumnFilters(true);
    } else {
      const resultFilters = table
        .getState()
        .columnFilters.map((f) => {
          // multiselect has value as an array
          if (f.id === filterId && value) {
            const newValue = Array.isArray(f.value) ? f.value.filter((v) => v !== value) : '';
            return { ...f, value: newValue };
          }
          return f;
        })
        .filter((f) => {
          if (Array.isArray(f.value)) {
            return f.value.length;
          } else if (!filterId) {
            return false;
          }
          return f.id !== filterId;
        });
      table.setColumnFilters(resultFilters);
    }
  };

  return (
    <Stack direction="column" gap="5" paddingBottom="5">
      <Stack direction="row" gap="4">
        {sortedFilters.map((group) => group.map((header) => <TableFilter key={header.id} column={header.column} />))}
      </Stack>
      {filters?.length ? <TableFilterControl filters={filters} onFilterClear={onFilterClear} /> : null}
    </Stack>
  );
};
